import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/terms.scss";

function Terms() {
  const currentYear = new Date().getFullYear();
  const contact = siteConfig.contact || {};

  return (
    <main className="content-page legal-page terms-page">
      <header className="page-hero page-hero--compact">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Site information</p>
          <h1>Terms &amp; conditions</h1>
          <p className="page-hero__lede">Last updated: {currentYear}</p>
          <p className="page-hero__lede">
            Demo terms template — review with qualified counsel before using it
            for live sales.
          </p>
        </div>
      </header>

      <section className="page-section page-shell legal-layout">
        <nav className="policy-nav" aria-label="Terms and conditions sections">
          <p className="eyebrow">Contents</p>
          <a href="#terms-agreement">Agreement</a>
          <a href="#terms-orders">Products &amp; orders</a>
          <a href="#terms-fulfillment">Shipping &amp; returns</a>
          <a href="#terms-use">Acceptable use</a>
          <a href="#terms-liability">Disclaimers</a>
          <a href="#terms-contact">Contact</a>
        </nav>

        <article className="prose legal-copy">
          <section id="terms-agreement">
            <h2>1. Agreement to these terms</h2>
            <p>
              These terms govern your access to the {siteConfig.brandName}{" "}
              demonstration website, content, and simulated shopping features.
              This project does not accept a real order or payment. Before a
              production launch, replace this template with approved terms and
              a matching <Link to="/privacy">privacy policy</Link>.
            </p>
            <p>
              You must be legally able to enter a binding contract and provide
              accurate, current checkout information. If you use the services
              for an organization, you represent that you can bind that
              organization.
            </p>
          </section>

          <section>
            <h2>2. Accounts and communications</h2>
            <p>
              You are responsible for safeguarding account credentials and
              activity under your account. Tell us promptly about suspected
              unauthorized use. Electronic order confirmations and service
              notices satisfy written communication requirements where permitted
              by law.
            </p>
          </section>

          <section id="terms-orders">
            <h2>3. Products, pricing, and availability</h2>
            <p>
              We try to present product descriptions, images, origins, sizes,
              prices, and availability accurately. Natural and small-batch foods
              can vary in color, texture, packaging, harvest, and flavor.
              Display settings can also affect how products appear.
            </p>
            <p>
              Prices and assortment may change without notice. Taxes and
              shipping charges are shown at checkout when applicable. If
              information is materially incorrect, we may correct it and offer
              you the choice to approve the change or cancel the affected item.
            </p>
          </section>

          <section>
            <h2>4. Orders and payment</h2>
            <p>
              Your checkout submission is an offer to purchase. An automated
              confirmation acknowledges receipt but does not guarantee
              acceptance. We may limit quantities or decline or cancel an order
              for suspected fraud, pricing errors, inventory shortages, delivery
              restrictions, or other legitimate reasons. If we cancel after
              payment, we will refund the affected amount to the original
              payment method.
            </p>
            <p>
              You authorize us and our payment providers to charge the amounts
              shown at checkout. You represent that you are authorized to use
              the selected payment method.
            </p>
          </section>

          <section id="terms-fulfillment">
            <h2>5. Shipping, returns, and food safety</h2>
            <p>
              Delivery dates are estimates, not guarantees. Ownership and risk
              of loss transfer as permitted by applicable law when the carrier
              completes delivery. See our{" "}
              <Link to="/shipping">shipping policy</Link> for destinations,
              rates, and delivery issues, and our{" "}
              <Link to="/returns">returns policy</Link> for eligibility and
              refund procedures.
            </p>
            <p>
              Follow package directions for storage, preparation, allergens, and
              use. Product labels control if site information differs. Contact a
              qualified professional about dietary or medical concerns; our
              content is general information, not medical advice.
            </p>
          </section>

          <section id="terms-use">
            <h2>6. Content and acceptable use</h2>
            <p>
              The site and its text, design, graphics, photography, recipes,
              trademarks, and software belong to {siteConfig.brandName} or its
              licensors and are protected by law. We grant you a limited,
              non-exclusive, revocable right to use the services for personal,
              non-commercial shopping.
            </p>
            <p>
              You may not interfere with the site, bypass security, introduce
              harmful code, scrape or copy content at scale, impersonate others,
              violate law, infringe rights, or use the services for fraudulent
              or unauthorized commercial activity.
            </p>
          </section>

          <section>
            <h2>7. Reviews and other submissions</h2>
            <p>
              You retain ownership of content you submit, but grant us a
              worldwide, non-exclusive, royalty-free license to host, reproduce,
              adapt, and display it for operating and promoting the services.
              You must have the necessary rights and must not submit unlawful,
              misleading, abusive, or infringing material. We may moderate or
              remove submissions.
            </p>
          </section>

          <section id="terms-liability">
            <h2>8. Disclaimers and limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, the site is provided “as
              is” and “as available,” without implied warranties of
              merchantability, fitness for a particular purpose, or
              non-infringement. We do not guarantee uninterrupted, error-free,
              or completely secure access.
            </p>
            <p>
              To the fullest extent permitted by law, {siteConfig.brandName} and
              its suppliers will not be liable for indirect, incidental,
              special, consequential, or punitive damages, or lost profits or
              data, arising from the services. Our aggregate liability for a
              claim will not exceed the amount you paid us for the order giving
              rise to that claim. These limits do not apply where prohibited by
              law.
            </p>
          </section>

          <section>
            <h2>9. Indemnity, law, and disputes</h2>
            <p>
              To the extent allowed by law, you agree to indemnify
              {` ${siteConfig.brandName} `}
              from third-party claims caused by your unlawful use of the
              services or violation of these terms. These terms are governed by
              the laws applicable where our principal business address is
              located, without regard to conflict-of-law rules. Before filing a
              formal claim, please contact us and allow 30 days for an informal
              resolution. Nothing here limits non-waivable consumer rights.
            </p>
          </section>

          <section>
            <h2>10. Changes and severability</h2>
            <p>
              We may revise these terms by posting an updated version and date.
              Your continued use after a change takes effect means you accept
              the revised terms. If one provision is unenforceable, the
              remaining provisions continue in effect. Our failure to enforce a
              term is not a waiver.
            </p>
          </section>

          <section id="terms-contact">
            <h2>11. Contact</h2>
            <p>
              Questions about these terms may be sent through our{" "}
              <Link to="/contact">contact page</Link>
              {contact.email && (
                <>
                  {" "}
                  or to <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </>
              )}
              .
            </p>
            {contact.address && <address>{contact.address}</address>}
          </section>
        </article>
      </section>
    </main>
  );
}

export default Terms;
